import React, { useState } from 'react';

import { ActionIcon, Box, Flex, createStyles } from '@mantine/core';
import { IconBold, IconItalic, IconLink, IconStrikethrough } from '@tabler/icons-react';
import { Editor } from '@tiptap/core';
import { BubbleMenu } from '@tiptap/react';
import { SmartEditorLinkInput } from 'pages/SmartEditor/SmartEditorLinkInput';
import { applyLinkOnSelection, applyTextOptionOnSelection, getSelectedLink } from 'shared/constants/editor';
import { Z_INDEX } from 'shared/constants/z-index';

interface SmartEditorBubbleMenuProps {
  editor: Editor;
  setIsLinkInputActive: React.Dispatch<React.SetStateAction<boolean>>;
}

const useStyles = createStyles((theme) => ({
  bubbleMenuContainer: {
    border: '1px solid',
    padding: '5px',
    borderRadius: '8px',
    borderColor: theme.colors.gray[2],
    boxShadow: theme.other.baseShadow,
    zIndex: Z_INDEX.FLOATING_ELEMENTS,
    backgroundColor: theme.white
  },
  linkInput: {
    input: {
      border: 'none',
      padding: '0px 4px'
    }
  },
  activeTextOption: {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.dark[4]
  }
}));

export function SmartEditorBubbleMenu({ editor, setIsLinkInputActive }: SmartEditorBubbleMenuProps) {
  const { classes, cx } = useStyles();
  const [showLinkInput, setShowLinkInput] = useState(false);

  const isBoldActive = editor.isActive('bold');
  const isItalicActive = editor.isActive('italic');
  const isStrikeActive = editor.isActive('strike');
  const isLinkActive = editor.isActive('link');
  const isTextSelected = editor.state.selection.$to.nodeBefore?.type.name === 'text';

  const handleLinkConfirm = (url: string) => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      setShowLinkInput(false);
      return;
    }

    // Apply the link, then make sure to focus the editor again
    applyLinkOnSelection(editor, trimmedUrl);
    setShowLinkInput(false);

    // Ensure the editor gets focus after applying the link
    setTimeout(() => {
      editor.commands.focus();
    }, 10);
  };

  const unsetLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  const handleLinkButtonClick = () => {
    if (isLinkActive) unsetLink();
    else setShowLinkInput(true);
  };

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        zIndex: Z_INDEX.FLOATING_ELEMENTS,
        hideOnClick: false,
        onHide: () => setShowLinkInput(false)
      }}>
      {isTextSelected && (
        <Box className={classes.bubbleMenuContainer}>
          {!showLinkInput ? (
            <Flex>
              <ActionIcon
                className={cx({ [classes.activeTextOption]: isBoldActive })}
                onClick={() => applyTextOptionOnSelection(editor, 'bold')}>
                <IconBold />
              </ActionIcon>

              <ActionIcon
                className={cx({ [classes.activeTextOption]: isItalicActive })}
                onClick={() => applyTextOptionOnSelection(editor, 'italic')}>
                <IconItalic />
              </ActionIcon>

              <ActionIcon
                className={cx({ [classes.activeTextOption]: isStrikeActive })}
                onClick={() => applyTextOptionOnSelection(editor, 'strike')}>
                <IconStrikethrough />
              </ActionIcon>

              <ActionIcon className={cx({ [classes.activeTextOption]: isLinkActive })} onClick={handleLinkButtonClick}>
                <IconLink />
              </ActionIcon>
            </Flex>
          ) : (
            <SmartEditorLinkInput
              onConfirm={handleLinkConfirm}
              onCancel={() => setShowLinkInput(false)}
              initialUrl={getSelectedLink(editor)}
              setIsLinkInputActive={setIsLinkInputActive}
            />
          )}
        </Box>
      )}
    </BubbleMenu>
  );
}
